import React, { useEffect, useMemo, useState } from 'react';
import { Activity, Download, Eye, Fingerprint, RefreshCcw, Search, ShieldAlert } from 'lucide-react';
import {
  superAdminCreateSecurityEvent,
  superAdminGetActivityLogs,
  superAdminGetAuditLogs,
  superAdminGetLoginHistory,
  superAdminGetSecurityEvents,
} from '../../api/api';
import StyledSelect from '../../components/StyledSelect';
import {
  exportRowsAsCsv,
  formatDateTime,
  SeverityChip,
  StatusChip,
  SuperAdminPanel,
} from '../components/SuperAdminUi';

const tabs = [
  { key: 'audit', label: 'Audit Logs', icon: Fingerprint },
  { key: 'activity', label: 'Activity Stream', icon: Activity },
  { key: 'logins', label: 'Login History', icon: ShieldAlert },
  { key: 'events', label: 'Security Events', icon: ShieldAlert },
];

const severityOptions = [
  { value: '', label: 'All severity levels' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const eventStatusOptions = [
  { value: '', label: 'All event statuses' },
  { value: 'open', label: 'Open' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'resolved', label: 'Resolved' },
];

const loginStatusOptions = [
  { value: '', label: 'All login statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'blocked', label: 'Blocked' },
];

const eventTypeOptions = [
  { value: 'suspicious_activity', label: 'Suspicious Activity' },
  { value: 'role_change', label: 'Role Change' },
  { value: 'organization_status_change', label: 'Organization Status Change' },
  { value: 'privileged_access', label: 'Privileged Access' },
  { value: 'data_export', label: 'Data Export' },
  { value: 'manual_review', label: 'Manual Review' },
];

const initialFilters = {
  search: '',
  from_date: '',
  to_date: '',
  action: '',
  status: '',
  severity: '',
};

export default function SuperAdminSecurity() {
  const [activeTab, setActiveTab] = useState('audit');
  const [filters, setFilters] = useState(initialFilters);
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, totalPages: 1, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [eventForm, setEventForm] = useState({ event_type: 'suspicious_activity', severity: 'medium', status: 'open', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const activeFetcher = useMemo(() => {
    if (activeTab === 'activity') return superAdminGetActivityLogs;
    if (activeTab === 'logins') return superAdminGetLoginHistory;
    if (activeTab === 'events') return superAdminGetSecurityEvents;
    return superAdminGetAuditLogs;
  }, [activeTab]);

  const activePayloadKey = activeTab === 'events' ? 'events' : 'logs';

  const tableColumns = useMemo(() => {
    if (activeTab === 'activity') {
      return [
        { key: 'action', label: 'Action' },
        { key: 'actor_email', label: 'User', value: (row) => row.actor_email || row.actor_name || '-' },
        { key: 'organization_name', label: 'Organization', value: (row) => row.organization_name || '-' },
        { key: 'entity_type', label: 'Target Type', value: (row) => row.entity_type || '-' },
        { key: 'entity_id', label: 'Target ID', value: (row) => row.entity_id || '-' },
        { key: 'ip_address', label: 'IP Address', value: (row) => row.ip_address || '-' },
        { key: 'user_agent', label: 'Device/Browser', value: (row) => row.device_label || row.user_agent || '-' },
        { key: 'created_at', label: 'Timestamp', value: (row) => formatDateTime(row.created_at) },
      ];
    }

    if (activeTab === 'logins') {
      return [
        { key: 'status', label: 'Status' },
        { key: 'email', label: 'User' },
        { key: 'organization_name', label: 'Organization', value: (row) => row.organization_name || '-' },
        { key: 'failure_reason', label: 'Description', value: (row) => row.failure_reason || 'Successful authentication' },
        { key: 'ip_address', label: 'IP Address', value: (row) => row.ip_address || '-' },
        { key: 'device_label', label: 'Device/Browser', value: (row) => row.device_label || '-' },
        { key: 'created_at', label: 'Timestamp', value: (row) => formatDateTime(row.created_at) },
      ];
    }

    if (activeTab === 'events') {
      return [
        { key: 'event_type', label: 'Action' },
        { key: 'user_email', label: 'User', value: (row) => row.user_email || 'System' },
        { key: 'organization_name', label: 'Organization', value: (row) => row.organization_name || '-' },
        { key: 'severity', label: 'Severity' },
        { key: 'status', label: 'Status' },
        { key: 'description', label: 'Description', value: (row) => row.description || '-' },
        { key: 'ip_address', label: 'IP Address', value: (row) => row.ip_address || '-' },
        { key: 'created_at', label: 'Timestamp', value: (row) => formatDateTime(row.created_at) },
      ];
    }

    return [
      { key: 'action', label: 'Action' },
      { key: 'actor_email', label: 'User', value: (row) => row.actor_email || row.actor_name || '-' },
      { key: 'organization_name', label: 'Organization', value: (row) => row.organization_name || '-' },
      { key: 'target_type', label: 'Target Type', value: (row) => row.target_type || '-' },
      { key: 'target_id', label: 'Target ID', value: (row) => row.target_id || '-' },
      { key: 'detail', label: 'Description', value: (row) => row.detail || '-' },
      { key: 'ip_address', label: 'IP Address', value: (row) => row.ip_address || '-' },
      { key: 'user_agent', label: 'Device/Browser', value: (row) => row.device_label || row.user_agent || '-' },
      { key: 'created_at', label: 'Timestamp', value: (row) => formatDateTime(row.created_at) },
    ];
  }, [activeTab]);

  const load = async (nextPage = 1, nextFilters = filters) => {
    setLoading(true);
    setError('');

    try {
      const response = await activeFetcher({
        page: nextPage,
        limit: meta.limit,
        search: nextFilters.search || undefined,
        action: activeTab === 'audit' || activeTab === 'activity' ? nextFilters.action || undefined : undefined,
        status: activeTab === 'logins' || activeTab === 'events' ? nextFilters.status || undefined : undefined,
        severity: activeTab === 'events' ? nextFilters.severity || undefined : undefined,
        from_date: nextFilters.from_date || undefined,
        to_date: nextFilters.to_date || undefined,
      });

      const data = response?.data?.data || {};
      setRecords(data[activePayloadKey] || []);
      setMeta({
        total: data.total || 0,
        page: data.page || nextPage,
        totalPages: data.totalPages || 1,
        limit: data.limit || meta.limit,
      });
    } catch (requestError) {
      setRecords([]);
      setError(requestError?.response?.data?.message || 'Failed to load security telemetry.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [activeTab]);

  const summaryCards = useMemo(() => {
    return [
      { label: 'Visible Rows', value: records.length },
      { label: 'Total Results', value: meta.total },
      { label: 'Open Events', value: records.filter((record) => record.status === 'open').length },
      { label: 'Critical Items', value: records.filter((record) => record.severity === 'critical').length },
    ];
  }, [meta.total, records]);

  const handleExport = () => {
    exportRowsAsCsv(records, tableColumns, `super-admin-${activeTab}.csv`);
  };

  const handleCreateEvent = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await superAdminCreateSecurityEvent(eventForm);
      setSuccess('Security event recorded successfully.');
      setEventForm({ event_type: 'suspicious_activity', severity: 'medium', status: 'open', description: '' });
      if (activeTab === 'events') {
        await load(1);
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to create security event.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="superadmin-title text-3xl font-bold text-slate-900">Audit & Security Center</h1>
          <p className="mt-1 text-slate-500">Platform-wide privileged activity, login intelligence, and incident operations in one responsive workspace.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => load(meta.page)} className="ems-btn-secondary flex items-center gap-2 text-sm">
            <RefreshCcw size={14} /> Refresh
          </button>
          <button type="button" onClick={handleExport} className="ems-btn-primary flex items-center gap-2 text-sm">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <section className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="superadmin-kpi rounded-2xl p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-slate-400">{card.label}</p>
            <p className="mt-3 text-2xl font-extrabold text-slate-900">{card.value}</p>
          </div>
        ))}
      </section>

      <section className="superadmin-glass rounded-3xl p-4 lg:p-5">
        <div className="flex flex-wrap gap-2">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setActiveTab(key);
                setSelectedRecord(null);
                setFilters(initialFilters);
              }}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${activeTab === key ? 'text-white shadow-md' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
              style={activeTab === key ? { background: 'linear-gradient(135deg,#c44569,#cf6a87)' } : undefined}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 xl:grid-cols-5">
          <label className="xl:col-span-2">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Search</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                value={filters.search}
                onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                placeholder="Search actions, users, descriptions, IP"
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-slate-800 outline-none transition focus:border-[#c44569] focus:ring-4 focus:ring-rose-100"
              />
            </div>
          </label>

          <DateField label="From">
            <input type="date" value={filters.from_date} onChange={(event) => setFilters((current) => ({ ...current, from_date: event.target.value }))} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none focus:border-[#c44569] focus:ring-4 focus:ring-rose-100" />
          </DateField>
          <DateField label="To">
            <input type="date" value={filters.to_date} onChange={(event) => setFilters((current) => ({ ...current, to_date: event.target.value }))} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none focus:border-[#c44569] focus:ring-4 focus:ring-rose-100" />
          </DateField>
          <ContextFilter activeTab={activeTab} filters={filters} setFilters={setFilters} />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={() => load(1)} className="ems-btn-primary text-sm">Apply Filters</button>
          <button type="button" onClick={() => { setFilters(initialFilters); load(1, initialFilters); }} className="ems-btn-secondary text-sm">Reset</button>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.5fr_1fr]">
        <section className="superadmin-glass rounded-3xl p-4 lg:p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">{tabs.find((tab) => tab.key === activeTab)?.label}</p>
              <p className="text-xs text-slate-500">Detailed privileged telemetry with export and record inspection.</p>
            </div>
          </div>

          {error ? <Banner tone="error" message={error} /> : null}
          {success ? <Banner tone="success" message={success} /> : null}

          <div className="space-y-3 lg:hidden">
            {loading ? <p className="text-sm text-slate-500">Loading records...</p> : null}
            {!loading && records.length === 0 ? <EmptyState /> : null}
            {!loading && records.map((record, index) => (
              <article key={record.id || index} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">{getRecordTitle(activeTab, record)}</p>
                    <p className="mt-1 text-sm text-slate-500">{getRecordActor(activeTab, record)}</p>
                  </div>
                  <RecordStatus activeTab={activeTab} record={record} />
                </div>
                <div className="mt-4 space-y-2 text-sm text-slate-600">
                  <p><span className="font-semibold text-slate-800">Organization:</span> {record.organization_name || '-'}</p>
                  <p><span className="font-semibold text-slate-800">IP:</span> {record.ip_address || '-'}</p>
                  <p><span className="font-semibold text-slate-800">Timestamp:</span> {formatDateTime(record.created_at)}</p>
                </div>
                <div className="mt-4 flex justify-end">
                  <button type="button" onClick={() => setSelectedRecord(record)} className="ems-btn-secondary flex items-center gap-2 text-sm">
                    <Eye size={14} /> View details
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="hidden overflow-auto lg:block">
            <table className="w-full min-w-[1180px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-slate-500">
                  {tableColumns.map((column) => (
                    <th key={column.label} className="py-3 pr-3">{column.label}</th>
                  ))}
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="py-8 text-center text-slate-500" colSpan={tableColumns.length + 1}>Loading records...</td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td className="py-8" colSpan={tableColumns.length + 1}><EmptyState compact /></td>
                  </tr>
                ) : records.map((record, index) => (
                  <tr key={record.id || index} className="border-b border-slate-50 align-top hover:bg-slate-50/60">
                    {tableColumns.map((column) => (
                      <td key={`${record.id || index}-${column.label}`} className="py-4 pr-3 text-slate-700">
                        {renderCell(activeTab, record, column)}
                      </td>
                    ))}
                    <td className="py-4 text-right">
                      <button type="button" onClick={() => setSelectedRecord(record)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100">
                        <Eye size={14} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">Page {meta.page} of {meta.totalPages}</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => load(Math.max(1, meta.page - 1))} disabled={meta.page <= 1 || loading} className="ems-btn-secondary text-sm disabled:opacity-50">Previous</button>
              <button type="button" onClick={() => load(Math.min(meta.totalPages, meta.page + 1))} disabled={meta.page >= meta.totalPages || loading} className="ems-btn-primary text-sm disabled:opacity-50">Next</button>
            </div>
          </div>
        </section>

        <section className="superadmin-glass rounded-3xl p-4 lg:p-5">
          <div>
            <p className="text-sm font-semibold text-slate-800">Raise Security Event</p>
            <p className="mt-1 text-xs text-slate-500">Document privileged incidents, escalations, and investigations as part of the enterprise control center.</p>
          </div>

          <form className="mt-4 space-y-3" onSubmit={handleCreateEvent}>
            <label>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Event Type</span>
              <StyledSelect value={eventForm.event_type} onChange={(value) => setEventForm((current) => ({ ...current, event_type: value }))} options={eventTypeOptions} placeholder="Select event type" searchable />
            </label>
            <label>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Severity</span>
              <StyledSelect value={eventForm.severity} onChange={(value) => setEventForm((current) => ({ ...current, severity: value }))} options={severityOptions.filter((item) => item.value)} placeholder="Select severity" />
            </label>
            <label>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Status</span>
              <StyledSelect value={eventForm.status} onChange={(value) => setEventForm((current) => ({ ...current, status: value }))} options={eventStatusOptions.filter((item) => item.value)} placeholder="Select status" />
            </label>
            <label>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Description</span>
              <textarea
                value={eventForm.description}
                onChange={(event) => setEventForm((current) => ({ ...current, description: event.target.value }))}
                rows={5}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#c44569] focus:ring-4 focus:ring-rose-100"
                placeholder="Describe the incident, privileged action, or review request."
              />
            </label>
            <button type="submit" disabled={submitting} className="ems-btn-primary w-full text-sm disabled:opacity-50">
              {submitting ? 'Recording event...' : 'Create Security Event'}
            </button>
          </form>
        </section>
      </div>

      <SuperAdminPanel
        open={!!selectedRecord}
        title={selectedRecord ? getRecordTitle(activeTab, selectedRecord) : ''}
        subtitle={selectedRecord ? getRecordActor(activeTab, selectedRecord) : ''}
        onClose={() => setSelectedRecord(null)}
      >
        {selectedRecord ? <RecordDetail activeTab={activeTab} record={selectedRecord} /> : null}
      </SuperAdminPanel>
    </div>
  );
}

function ContextFilter({ activeTab, filters, setFilters }) {
  if (activeTab === 'events') {
    return (
      <label>
        <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Severity</span>
        <StyledSelect value={filters.severity} onChange={(value) => setFilters((current) => ({ ...current, severity: value }))} options={severityOptions} placeholder="Filter severity" />
      </label>
    );
  }

  if (activeTab === 'logins') {
    return (
      <label>
        <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Status</span>
        <StyledSelect value={filters.status} onChange={(value) => setFilters((current) => ({ ...current, status: value }))} options={loginStatusOptions} placeholder="Filter login status" />
      </label>
    );
  }

  return (
    <label>
      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Action</span>
      <input
        value={filters.action}
        onChange={(event) => setFilters((current) => ({ ...current, action: event.target.value }))}
        placeholder="Exact action filter"
        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#c44569] focus:ring-4 focus:ring-rose-100"
      />
    </label>
  );
}

function DateField({ label, children }) {
  return (
    <label>
      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function Banner({ tone, message }) {
  const style = tone === 'error'
    ? { background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }
    : { background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' };

  return <div className="mb-4 rounded-2xl border px-4 py-3 text-sm font-medium" style={style}>{message}</div>;
}

function EmptyState({ compact = false }) {
  return (
    <div className={`rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-center ${compact ? 'p-6' : 'p-10'}`}>
      <p className="text-sm font-semibold text-slate-700">No records matched the current security filters.</p>
      <p className="mt-1 text-sm text-slate-500">Broaden the date range or clear the action, severity, and status filters.</p>
    </div>
  );
}

function renderCell(activeTab, record, column) {
  if (column.key === 'severity') {
    return <SeverityChip severity={record.severity} />;
  }

  if (column.key === 'status') {
    return <StatusChip status={record.status} />;
  }

  return column.value ? column.value(record) : record[column.key] || '-';
}

function RecordStatus({ activeTab, record }) {
  if (activeTab === 'events') {
    return <SeverityChip severity={record.severity} />;
  }

  if (activeTab === 'logins') {
    return <StatusChip status={record.status} />;
  }

  return <StatusChip status={record.status || 'active'} />;
}

function getRecordTitle(activeTab, record) {
  if (activeTab === 'events') return record.event_type || 'Security event';
  if (activeTab === 'logins') return `Login ${record.status || 'event'}`;
  return record.action || 'Audit record';
}

function getRecordActor(activeTab, record) {
  if (activeTab === 'events') return record.user_email || 'System';
  if (activeTab === 'logins') return record.email || record.user_name || 'Unknown user';
  return record.actor_email || record.actor_name || 'System';
}

function RecordDetail({ activeTab, record }) {
  const rows = [
    ['Action', activeTab === 'events' ? record.event_type : record.action || record.status || '-'],
    ['User', getRecordActor(activeTab, record)],
    ['Organization', record.organization_name || '-'],
    ['Target Type', record.target_type || record.entity_type || '-'],
    ['Target ID', record.target_id || record.entity_id || '-'],
    ['Description', record.detail || record.description || record.failure_reason || '-'],
    ['IP Address', record.ip_address || '-'],
    ['Device/Browser', record.device_label || record.user_agent || '-'],
    ['Timestamp', formatDateTime(record.created_at)],
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label}>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{label}</p>
            <div className="mt-1 text-sm font-medium text-slate-700">{value}</div>
          </div>
        ))}
      </div>

      {record.payload ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-sm font-semibold text-slate-800">Payload</p>
          <pre className="mt-3 overflow-auto rounded-2xl bg-slate-950 p-4 text-xs text-slate-100">{formatPayload(record.payload)}</pre>
        </div>
      ) : null}
    </div>
  );
}

function formatPayload(payload) {
  try {
    const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(payload);
  }
}